import { clsx } from '@alexis/helpers/clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getIsRtlState, getTranslationsState } from '@redux/selectors';

import { BUSINESS_CARDS } from '../../constants/wegoProDropdown';
import styles from './DropdownWegoPro.module.scss';
import { DropdownWegoProProps, CardData } from './type';

const DropdownWegoPro = ({ wegoProLink, onWegoProClick }: DropdownWegoProProps) => {
  const [cards, setCards] = useState<CardData[]>([]);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const isRtl = useSelector(getIsRtlState);
  const translations = useSelector(getTranslationsState);

  // Initialize cards only once
  useEffect(() => {
    const initializedCards = BUSINESS_CARDS.map((card, index) => ({
      ...card,
      position: index === 0 ? 'front' : index === 1 ? 'middle' : 'back',
    }));

    setCards(initializedCards as CardData[]);
  }, []);

  // Function to rotate cards with animation
  const rotateCards = useCallback(() => {
    if (isAnimating || cards.length < 3) return;

    setIsAnimating(true);

    // Create a new array to avoid mutating state
    const updatedCards = cards.map((card, index) => ({
      ...card,
      position: index === 0 ? 'front-to-back' : index === 1 ? 'middle-to-front' : 'back-to-middle',
    }));

    setCards(updatedCards as CardData[]);

    // Then, after animation completes, actually rotate the array
    setTimeout(() => {
      // Create a new array and move first card to the end
      const rotatedCards = [...cards.slice(1), cards[0]].map((card, index) => ({
        ...card,
        position: index === 0 ? 'front' : index === 1 ? 'middle' : 'back',
      }));

      setCards(rotatedCards as CardData[]);
      setIsAnimating(false);
    }, 1000); // 1000ms to match animation duration
  }, [cards, isAnimating]);

  // Auto-rotate cards - stable interval that doesn't recreate on every render
  useEffect(() => {
    if (cards.length < 3) return;

    const interval = setInterval(() => {
      rotateCards();
    }, 3000);

    return () => clearInterval(interval);
  }, [rotateCards, cards.length]);

  return (
    <div className={styles.dropdownContainer}>
      <i className={clsx(styles.triangle, styles.down)}></i>
      <div className={styles.content}>
        <div className={styles.header}>
          <h3 className={styles.title}>{translations['wego_pro_modal_title']}</h3>
          <p className={styles.subtitle}>{translations['wego_pro_modal_desc']}</p>
        </div>

        <div className={styles.carouselContainer}>
          {cards.map((card) => (
            <div
              key={card.id}
              className={`${styles.card} ${styles[card.position || 'front']}`}
              style={{ backgroundColor: card.bgColor }}
            >
              <div className={styles.cardContent}>
                <div className={styles.cardText}>
                  <h4 className={styles.cardTitle}>
                    {translations[`${card.title_translation_key}`]}
                  </h4>
                  <p className={styles.cardDescription}>
                    {translations[`${card.description_translation_key}`]}
                  </p>
                </div>
                <div
                  className={styles.cardImage}
                  style={{ backgroundImage: `url(${isRtl ? card.imageSrcAr : card.imageSrc})` }}
                />
              </div>
            </div>
          ))}
        </div>

        <a
          href={wegoProLink}
          target='_blank'
          rel='noreferrer'
          className={styles.continueButton}
          onClick={onWegoProClick}
        >
          {`${translations['continue']} ${translations['small_to']} WegoPro`}
        </a>
      </div>
    </div>
  );
};

export default DropdownWegoPro;
